import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

const styles = {
  error: {
    box: 'border-danger/30 bg-danger/10 text-danger',
    Icon: AlertCircle,
    role: 'alert' as const,
  },
  success: {
    box: 'border-success/30 bg-success/10 text-success',
    Icon: CheckCircle2,
    role: 'status' as const,
  },
  info: {
    box: 'border-info/30 bg-info/10 text-info',
    Icon: Info,
    role: 'status' as const,
  },
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
  onDismiss,
  dismissLabel = 'Dismiss',
}: {
  tone?: keyof typeof styles
  title?: string
  children?: ReactNode
  className?: string
  /** Renders a close button. Used by the toaster; inline alerts usually stay put. */
  onDismiss?: () => void
  dismissLabel?: string
}) {
  const { box, Icon, role } = styles[tone]

  return (
    <div role={role} className={cn('flex gap-3 rounded-md border p-3 text-sm', box, className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className="opacity-90">{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-m-1 h-6 w-6 shrink-0 rounded p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
